import { ReactElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Link, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdDocument,
  FeatureSubscriptionType,
  useFeatureSubscription_UpdateStripeSubscriptionMutation,
} from 'queries';

import Modal from 'common/modal';
import Spinner from 'common/spinner';
import {
  getNameForFeatureSubscriptionType,
  getProductCodeForProductType,
} from 'utils/feature-subscription';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

type Props = {
  setShowModal: (value: boolean) => void;
  showModal: boolean;
  minimumFeatureSubscriptionType: FeatureSubscriptionType | null;
};

const useStyles = makeStyles(() => ({
  upgradeButton: {
    backgroundColor: '#1C2541',
    color: 'white',
    fontWeight: 400,
    borderRadius: '100px',
    width: '90px',
    whiteSpace: 'nowrap',
    minWidth: 'max-content',
  },
}));

export default function PlanPaywallModal({
  minimumFeatureSubscriptionType,
  setShowModal,
  showModal,
}: Props) {
  const classes = useStyles();
  const history = useHistory();

  const { portalId } = useParams<Record<string, string>>();
  const { estateId } = useParams<Record<string, string>>();

  const [
    updateFeatureSubscriptionMutation,
    { loading: updateFeatureSubscriptionMutationExecuting },
  ] = useFeatureSubscription_UpdateStripeSubscriptionMutation({
    refetchQueries: [
      {
        query: EstateTaskInstance_GetAllEstateTaskInstancesByEstateIdDocument,
        variables: { input: { estateId, portalId } },
      },
    ],
    onCompleted: (data) => {
      if (!data.data || !data.data.ok) {
        showAlert({
          message: 'Error updating stripe subscription',
          severity: NotificationSeverity.ERROR,
        });
        return;
      }

      showToast({
        message: 'Subscription has been updated.',
        severity: NotificationSeverity.SUCCESS,
      });
      setShowModal(false);
      // This is required to refresh the page after subscription update
      // eslint-disable-next-line no-self-assign
      window.location.href = window.location.href;
    },
    onError: (e) => {
      showAlert({
        message: 'Error updating stripe subscription: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  if (!minimumFeatureSubscriptionType) {
    return <Spinner />;
  }

  const plan = getNameForFeatureSubscriptionType(minimumFeatureSubscriptionType);

  const onExitClick = () => {
    setShowModal(false);
  };

  const handleOnUpgradeSubscriptionLinkClick = async () => {
    history.push(`/home/${portalId}/subscription-checkout?isUpdating=True`);
  };

  const handleOnUpgradeSubscriptionButtonClick = async () => {
    const nextPlan = getProductCodeForProductType(minimumFeatureSubscriptionType);
    if (!nextPlan) {
      showAlert({
        message: 'An error occurred updating your subscription.',
        severity: NotificationSeverity.ERROR,
      });
      return;
    }

    await updateFeatureSubscriptionMutation({
      variables: {
        input: {
          portalId,
          selectedPlan: nextPlan,
          additionalSeats: 0, // remove any additional seats that may be on the account
        },
      },
    });
  };

  const planPaywallContent = () => {
    let prompt: ReactElement[] = [
      <Typography variant="body1">Upgrade your plan to push this task to Trustate.</Typography>,
    ];
    if (minimumFeatureSubscriptionType) {
      prompt.push(
        <Typography variant="body1" display="inline">
          This task requires a{' '}
          <Link
            component="button"
            variant="body2"
            underline="always"
            key="upgrade-link"
            onClick={handleOnUpgradeSubscriptionLinkClick}
            style={{ display: 'inline', padding: 0 }}
          >
            <strong>{plan}</strong>
          </Link>{' '}
          subscription plan or higher.
        </Typography>,
      );
    }

    return (
      <div>
        <h2>This task is not included in your subscription plan.</h2>
        <p>{prompt}</p>
        <Button
          className={classes.upgradeButton}
          component="span"
          variant="contained"
          size="small"
          aria-controls="upgrade"
          aria-haspopup="true"
          onClick={handleOnUpgradeSubscriptionButtonClick}
          disabled={updateFeatureSubscriptionMutationExecuting}
        >
          Upgrade to {plan}
        </Button>
      </div>
    );
  };

  return (
    <Modal
      setShowModal={setShowModal}
      onExitClick={onExitClick}
      showModal={showModal}
      showActions={false}
      content={planPaywallContent()}
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
}
